import { Link } from '@atlassiansox/wpl-utils';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import React from 'react';

import { BaseDownloadContainer } from './download-pdf.styles';
import PdfLayout from './pdf-layout';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import {
  ANALYTICS_EVENTS,
  ICONS_BY_PRODUCT_NAME,
  PRODUCT_FAMILY_ICONS_BY_NAME,
  PRODUCT_NAMES_BY_PRODUCT_FAMILY_NAME
} from '../../constants';
import { useModuleProps } from '../../module-props';
import { useGlobalStore } from '../../store';
import {
  useHighestUnitCount,
  useSelectedProductFamilyNames,
  useTotal
} from '../../selectors';
import { ProductFamilyName, SelectedProductDetails } from '../../models';
import { appLogo } from '../marketplace-app-selector/app-card/app-logo';

const DownloadPdf = () => {
  const pdfContentrefObj = React.useRef<HTMLDivElement | null>(null);
  const { createAnalyticsEvent } = useAnalyticsEvents();
  const { downloadPdfLabel, appDisclaimerLabel, usersLabel } = useModuleProps();
  const [
    {
      selectedBillingCycle,
      selectedProductsWithDetails,
      selectedMarketplaceAddons,
      selectedAppsWithDetails
    }
  ] = useGlobalStore();
  const [formattedPrice] = useTotal();
  const [selectedProductFamilyNames] = useSelectedProductFamilyNames();
  const [highestUnitCount] = useHighestUnitCount('Jira products');

  const buildContent = selectedProductFamilyNames.map(
    (productFamilyName: ProductFamilyName) => {
      const productsWithDetails: SelectedProductDetails[] = [];
      for (const [key, value] of selectedProductsWithDetails) {
        if (
          PRODUCT_NAMES_BY_PRODUCT_FAMILY_NAME[productFamilyName]?.includes(key)
        ) {
          productsWithDetails.push(value);
        }
      }

      const selectedProducts = productsWithDetails.map((product) => {
        const {
          name,
          planName,
          selectedUserCount,
          costPerUserType,
          monthlyCost,
          userType,
          userTypeTier
        } = product;
        return {
          name,
          logo: ICONS_BY_PRODUCT_NAME[name] as React.ReactElement,
          selectedPlanName: planName,
          selectedUserCount,
          costPerUserType,
          monthlyCost,
          userType,
          userTypeTier
        };
      });

      const selectedAppNames: string[] = [];
      selectedMarketplaceAddons
        .get(productFamilyName)
        ?.forEach((item) => selectedAppNames.push(item.name));

      const finalApps = [];

      for (const [key, value] of selectedAppsWithDetails) {
        if (selectedAppNames.includes(key)) {
          finalApps.push({ ...value, logo: appLogo });
        }
      }

      const productFamily = {
        familyName: productFamilyName,
        logo: PRODUCT_FAMILY_ICONS_BY_NAME[productFamilyName],
        selectedProducts,
        apps: finalApps,
        billingMessage:
          productFamilyName === 'Jira products' &&
          highestUnitCount > 0 &&
          selectedAppNames.length > 0
            ? `${appDisclaimerLabel} ${highestUnitCount} ${usersLabel}.`
            : null
      };
      return productFamily;
    }
  );

  const creatingPdf = async () => {
    const analyticsEvent = createAnalyticsEvent?.({
      action: 'click',
      component: 'button',
      eventName: ANALYTICS_EVENTS.downloadPdf
    });

    analyticsEvent?.fire();

    const pdfCurrentData = pdfContentrefObj.current;

    if (pdfCurrentData) {
      pdfCurrentData.style.visibility = 'visible';
      const canvasEle = await html2canvas(pdfCurrentData);
      const imgContent = canvasEle.toDataURL('image/png');
      const jspdfObj = new jsPDF('portrait', 'mm', 'a4', true);
      const imgProps = jspdfObj.getImageProperties(imgContent);
      const pdfWidth = 190; // Width of A4 paper in mm
      const pageHeight = 290;
      const pdfHeight = (pdfWidth * imgProps.height) / imgProps.width;
      let heightLeft = pdfHeight;
      let position = 0;
      heightLeft -= pageHeight;
      jspdfObj.addImage(
        imgContent,
        'PNG',
        10,
        0,
        pdfWidth,
        pdfHeight + 10,
        'FAST'
      );
      while (heightLeft >= 0) {
        position = heightLeft - pdfHeight;
        jspdfObj.addPage();
        jspdfObj.addImage(
          imgContent,
          'PNG',
          10,
          position,
          pdfWidth,
          pdfHeight + 10,
          'FAST'
        );
        heightLeft -= pageHeight;
      }
      const numberOfPages = jspdfObj.getNumberOfPages();

      for (let i = 0; i < numberOfPages; i++) {
        jspdfObj.setPage(i);
        const pageCurrent = jspdfObj.getCurrentPageInfo().pageNumber; //Current Page
        jspdfObj.setFontSize(10);
        jspdfObj.text(
          'Page: ' + pageCurrent + ' of ' + numberOfPages,
          185,
          290
        );
      }
      pdfCurrentData.style.visibility = 'hidden';
      const date = new Date();
      const month = (date.getMonth() + 1).toString();

      const day = date.getDate().toString();

      const year = date.getFullYear().toString();
      const currentDate = month + day + year;

      const pdfTitle = `AtlassianEstimate_${currentDate}`;

      // Saving the PDF
      jspdfObj.save(pdfTitle);
    } else {
      console.error('The data is not available in the pdf');
    }
  };

  return (
    <div css={BaseDownloadContainer}>
      <Link
        onClick={creatingPdf}
        text={downloadPdfLabel}
        href={''}
        type="external"
      />
      <div
        ref={pdfContentrefObj}
        style={{
          visibility: 'hidden',
          position: 'absolute',
          left: '-10000px',
          top: '-10000px'
        }}
      >
        <PdfLayout
          //@ts-ignore
          selectedProductFamilies={buildContent}
          billingCycle={selectedBillingCycle}
          totalCost={formattedPrice}
          currency={'USD'}
        />
      </div>
    </div>
  );
};

export default DownloadPdf;
